import type {
  AddGuestRequest,
  Organization,
  Patrol,
  Person,
} from '@local/common-lib';
import { formatDuration } from '@local/common-lib';
import {
  CheckCircle as AcceptIcon,
  Cancel as DeclineIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import type { Theme } from '@mui/material';
import {
  CardActions,
  CardContent,
  CardHeader,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { DateTime, Duration } from 'luxon';
import type { FC } from 'react';
import { ConfirmButton } from '../../components/confirm-button/index.js';
import { CenterCard } from '../../components/page-layout/center-card.js';

export interface ViewProps {
  accept: () => void;
  assignment: Patrol.Assignment;
  busy: boolean;
  decline: () => void;
  guests: AddGuestRequest[];
  me: Person.View;
  organization: Organization.Type;
  patrol: Omit<Patrol.Type, 'guests' | 'patrollers'>;
  patrollers: Person.View[];
}

export interface DoneViewProps {
  me: Person.View;
}

export const AcceptDeclineView: FC<ViewProps> = ({
  assignment,
  busy,
  guests,
  organization,
  patrol,
  patrollers,
  me,
  accept,
  decline,
}) => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const isBot = navigator.userAgent.toLowerCase().includes('headless');

  const canChange =
    !isBot &&
    (!assignment.accepted ||
      (assignment.accepted.when &&
        DateTime.fromJSDate(assignment.accepted.when) <
          DateTime.now().minus({ minutes: 5 })));

  return (
    <CenterCard>
      {!patrol.finalized && (
        <CardHeader
          sx={{
            backgroundColor: (theme) => theme.palette.warning.main,
            color: (theme) => theme.palette.warning.contrastText,
            p: 1.5,
            textAlign: 'center',
          }}
          title="NOT FINALIZED"
        />
      )}
      {assignment.accepted && (
        <CardHeader
          sx={{
            backgroundColor: (theme) => theme.palette.success.main,
            color: (theme) => theme.palette.success.contrastText,
            p: 1.5,
            textAlign: 'center',
          }}
          title="ACCEPTED"
        />
      )}
      {assignment.declined && (
        <CardHeader
          sx={{
            backgroundColor: (theme) => theme.palette.error.main,
            color: (theme) => theme.palette.error.contrastText,
            p: 1.5,
            textAlign: 'center',
          }}
          title="DECLINED"
        />
      )}
      <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant={smallScreen ? 'h5' : 'h4'}>
          Patrol Assignment
        </Typography>
      </CardContent>
      <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ mb: 2 }}>
          {me.firstName} you have been assigned to this patrol for{' '}
          {organization.name}.
        </Typography>

        <Typography sx={{ mb: 2 }}>
          {patrol.name ? (
            <>
              <strong>Patrol: </strong>
              {patrol.name}
              <br />
            </>
          ) : undefined}
          <strong>Patrol date: </strong>
          {DateTime.fromJSDate(patrol.when).toLocaleString({
            day: 'numeric',
            month: 'short',
            weekday: 'short',
          })}
          <br />
          <strong>Start time: </strong>
          {DateTime.fromJSDate(patrol.when).toLocaleString(
            DateTime.TIME_SIMPLE,
          )}
          <br />
          <strong>Duration: </strong>
          {formatDuration(Duration.fromObject({ hours: patrol.hours }))}
        </Typography>

        <Typography>Your patrol team will be:</Typography>

        <ul>
          {patrollers.map((patroller) => (
            <li key={patroller._id}>
              <Typography>
                {patroller.firstName} {patroller.lastName.toUpperCase()}
              </Typography>
            </li>
          ))}
          {guests.map((guest) => (
            <li key={guest.name}>
              <Typography>{guest.name} (guest)</Typography>
            </li>
          ))}
        </ul>

        {canChange && (
          <Typography>
            Please confirm your availability by choosing to{' '}
            <strong>Accept</strong> or <strong>Decline</strong> this patrol
            using the buttons below.
          </Typography>
        )}
      </CardContent>
      {canChange && (
        <CardActions sx={{ justifyContent: 'space-between', mb: 1, mx: 1 }}>
          <ConfirmButton
            busy={busy}
            color="error"
            disabled={!!assignment.declined}
            onClick={decline}
            startIcon={<DeclineIcon />}
            variant="contained"
          >
            Decline
          </ConfirmButton>
          <LoadingButton
            color="success"
            disabled={!!assignment.accepted}
            loading={busy}
            onClick={accept}
            startIcon={<AcceptIcon />}
            variant="contained"
          >
            Accept
          </LoadingButton>
        </CardActions>
      )}
    </CenterCard>
  );
};

export const AcceptDeclineDoneView: FC<DoneViewProps> = () => {
  return (
    <CenterCard>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', py: 3 }}>
        <Typography variant="body1">Thank you.</Typography>
      </CardContent>
    </CenterCard>
  );
};
